import { useState, useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hook';
import {
  getAllCompaniesDetails,
  getAssociateCompanies,
  getLocations,
  getStates,
  getCompanyEmployerSubContractors
} from '../redux/features/inputModule.slice';
import {
  getStateRegisterQue,
  addStateRegisterQue
} from '../redux/features/stateRegisterQue.slice';
import { DEFAULT_OPTIONS_PAYLOAD } from '../components/common/Table';
import {
  FilterState,
  SearchConfig,
  INITIAL_FILTER_STATE,
  INITIAL_SEARCH_CONFIG,
  buildQueryPayload,
  getResetFields,
  getFilterKey,
  INCLUDE_ASSOCIATION_COLUMNS_CLRA,
  INCLUDES_ASSOCIATIONS_CLRA
} from '../utils/registersQue.utils';

export const useFilters = () => {
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTER_STATE);

  const updateFilter = useCallback((field: keyof FilterState, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      ...getResetFields(field)
    }));
  }, []);

  const updateMultipleFilters = useCallback((updates: Partial<FilterState>) => {
    setFilters(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(INITIAL_FILTER_STATE);
  }, []);

  return { filters, updateFilter, updateMultipleFilters, resetFilters, setFilters };
};

export const useSearchConfig = () => {
  const [searchConfig, setSearchConfig] = useState<SearchConfig>(INITIAL_SEARCH_CONFIG);

  const updateSearchConfig = useCallback((updates: Partial<SearchConfig>) => {
    setSearchConfig(prev => ({ ...prev, ...updates }));
  }, []);

  const resetSearchConfig = useCallback(() => {
    setSearchConfig(INITIAL_SEARCH_CONFIG);
  }, []);

  return { searchConfig, updateSearchConfig, resetSearchConfig };
};

export const useStateRegisterData = (filters: FilterState, searchConfig: SearchConfig) => {
  const dispatch = useAppDispatch();

  const fetchData = useCallback((
    filtersOverride?: FilterState,
    searchConfigOverride?: Partial<SearchConfig>,
    overrides: Partial<any> = {},
    sortField?: string,
    sortDirection?: 'asc' | 'desc'
  ) => {
    const effectiveFilters = filtersOverride || filters;
    const effectiveSearchConfig = searchConfigOverride ? { ...searchConfig, ...searchConfigOverride } : searchConfig;
    const payload = buildQueryPayload(effectiveFilters, effectiveSearchConfig, overrides, sortField, sortDirection);
    dispatch(getStateRegisterQue(payload));
  }, [filters, searchConfig, dispatch]);

  const fetchWithSearch = useCallback((searchInput: string) => {
    const basePayload = buildQueryPayload(
      filters,
      searchConfig,
      { que_id_eq: searchInput },
      undefined,
      undefined
    );
    // Override to always request CLRA includes when searching (don't know type beforehand)
    const payload = {
      ...basePayload,
      include: INCLUDE_ASSOCIATION_COLUMNS_CLRA,
      includes: INCLUDES_ASSOCIATIONS_CLRA
    };
    dispatch(getStateRegisterQue(payload));
  }, [filters, searchConfig, dispatch]);

  return { fetchData, fetchWithSearch };
};

export const useCompanyData = () => {
  const dispatch = useAppDispatch();
  const companiesDetails = useAppSelector((state) => state.inputModule.companiesDetails);
  const associateCompaniesDetails = useAppSelector((state) => state.inputModule.associateCompaniesDetails);
  const locationsDetails = useAppSelector((state) => state.inputModule.locationsDetails);
  const statesDetails = useAppSelector((state) => state.inputModule.statesDetails);

  const companies = ((companiesDetails|| {}).data || {}).items || [];
  const associateCompanies = ((associateCompaniesDetails || {}).data || {}).items || [];
  const locations = ((locationsDetails || {}).data || {}).items || [];
  const totalStates = ((statesDetails || {}).data || {}).items || [];

  const fetchCompanies = useCallback(() => {
    const payload = { ...DEFAULT_OPTIONS_PAYLOAD, filters: { is_parent_eq: true, is_active_eq: true } };
    dispatch(getAllCompaniesDetails(payload));
  }, [dispatch]);

  const fetchAssociateCompanies = useCallback((companyId: string) => {
    const payload = { 
      ...DEFAULT_OPTIONS_PAYLOAD, 
      filters: { is_parent_eq: false, parent_company_id_eq: companyId } 
    };
    dispatch(getAssociateCompanies(payload));
  }, [dispatch]);

  const fetchLocations = useCallback((associateCompanyId: string) => {
    const payload = {
      ...DEFAULT_OPTIONS_PAYLOAD,
      filters: { company_id_eq: associateCompanyId },
      includes: {
        location: { city: ['state'] }
      },
      include: {
        location: {
          only: ['id', 'name', 'code'],
          include: {
            city: {
              only: ['id', 'name', 'code'],
              include: {
                state: {
                  only: ['id', 'name', 'code']
                }
              }
            }
          }
        }
      },
      sorts: [`location_name asc`]
    };
    dispatch(getLocations(payload));
  }, [dispatch]);

  const fetchStates = useCallback(() => {
    const payload = { ...DEFAULT_OPTIONS_PAYLOAD };
    dispatch(getStates(payload));
  }, [dispatch]);

  return {
    companies,
    associateCompanies,
    locations,
    totalStates,
    fetchCompanies,
    fetchAssociateCompanies,
    fetchLocations,
    fetchStates
  };
};

export const useEmployerData = () => {
  const dispatch = useAppDispatch();
  const [employers, setEmployers] = useState<any[]>([]);
  const [employerLocations, setEmployerLocations] = useState<any[]>([]);
  const [employerSubContractors, setEmployerSubContractors] = useState<any[]>([]);
  const [employerSubContractorsLocations, setEmployerSubContractorsLocations] = useState<any[]>([]);

  const buildEmployerPayload = useCallback((
    filters: FilterState,
    locations: any[],
    payloadType: 'employer' | 'employerLocation' | 'subContractor' | 'subContractorLocation'
  ) => {
    const loc = locations.find((l: any) => l.location_id === filters.locations[0]);
    const basePayload: any = {
      filters: {
        establishment_type_eq: filters.establishmentType || '',
        company_id_eq: filters.company || '',
        associate_company_id_eq: filters.associateCompany || '',
        company_location_mapping_id_eq: (loc || {}).id
      },
      per: 5000,
      page: 1,
      includeCentral: true
    };

    switch (payloadType) {
      case 'employer':
        basePayload.include = {
          employer: {
            only: ['id', 'name']
          }
        };
        break;
      case 'employerLocation':
        basePayload.filters['employer_id_eq'] = filters.employer || null;
        basePayload.include = {
          employer_location: {
            only: ['id', 'location_name']
          }
        };
        break;
      case 'subContractor':
        basePayload.filters['employer_id_eq'] = filters.employer || null;
        basePayload.filters['employer_location_id_eq'] = filters.employerLocation || null;
        basePayload.include = {
          sub_contractor: {
            only: ['id', 'name']
          }
        };
        break;
      case 'subContractorLocation':
        basePayload.filters['employer_id_eq'] = filters.employer || null;
        basePayload.filters['employer_location_id_eq'] = filters.employerLocation || null;
        basePayload.filters['sub_contractor_id_eq'] = filters.subContractor || null;
        basePayload.include = {
          sub_contractor_location: {
            only: ['id', 'location_name']
          }
        };
        break;
    }

    return basePayload;
  }, []);

  const fetchEmployers = useCallback((filters: FilterState, locations: any[]) => {
    const payload = buildEmployerPayload(filters, locations, 'employer');
    dispatch(getCompanyEmployerSubContractors(payload)).then((response: any) => {
      if (response && response.payload && response.payload.data && response.payload.data.items) {
        const items = response.payload.data.items || [];
        const tempEmployers: any[] = [];
        items.forEach((x: any) => {
          if (x.employer_id && x.employer) {
            if (!tempEmployers.some((y: any) => y.id === x.employer.id)) {
              tempEmployers.push(x.employer);
            }
          }
        });
        setEmployers(tempEmployers);
      } else {
        setEmployers([]);
      }
    });
  }, [dispatch, buildEmployerPayload]);

  const fetchEmployerLocations = useCallback((filters: FilterState, locations: any[]) => {
    const payload = buildEmployerPayload(filters, locations, 'employerLocation');
    dispatch(getCompanyEmployerSubContractors(payload)).then((response: any) => {
      if (response && response.payload && response.payload.data && response.payload.data.items) {
        const items = response.payload.data.items || [];
        const tempLocations: any[] = [];
        items.forEach((x: any) => {
          if (x.employer_location_id && x.employer_location) {
            if (!tempLocations.some((y: any) => y.id === x.employer_location.id)) {
              tempLocations.push(x.employer_location);
            }
          }
        });
        setEmployerLocations(tempLocations);
      } else {
        setEmployerLocations([]);
      }
    });
  }, [dispatch, buildEmployerPayload]);

  const fetchSubContractors = useCallback((filters: FilterState, locations: any[]) => {
    const payload = buildEmployerPayload(filters, locations, 'subContractor');
    dispatch(getCompanyEmployerSubContractors(payload)).then((response: any) => {
      if (response && response.payload && response.payload.data && response.payload.data.items) {
        const items = response.payload.data.items || [];
        const tempSubContractors: any[] = [];
        items.forEach((x: any) => {
          if (x.sub_contractor_id && x.sub_contractor) {
            if (!tempSubContractors.some((y: any) => y.id === x.sub_contractor.id)) {
              tempSubContractors.push(x.sub_contractor);
            }
          }
        });
        setEmployerSubContractors(tempSubContractors);
      } else {
        setEmployerSubContractors([]);
      }
    });
  }, [dispatch, buildEmployerPayload]);

  const fetchSubContractorLocations = useCallback((filters: FilterState, locations: any[]) => {
    const payload = buildEmployerPayload(filters, locations, 'subContractorLocation');
    dispatch(getCompanyEmployerSubContractors(payload)).then((response: any) => {
      if (response && response.payload && response.payload.data && response.payload.data.items) {
        const items = response.payload.data.items || [];
        const tempLocations: any[] = [];
        items.forEach((x: any) => {
          if (x.sub_contractor_location_id && x.sub_contractor_location) {
            if (!tempLocations.some((y: any) => y.id === x.sub_contractor_location.id)) {
              tempLocations.push(x.sub_contractor_location);
            }
          }
        });
        setEmployerSubContractorsLocations(tempLocations);
      } else {
        setEmployerSubContractorsLocations([]);
      }
    });
  }, [dispatch, buildEmployerPayload]);

  return {
    employers,
    employerLocations,
    employerSubContractors,
    employerSubContractorsLocations,
    fetchEmployers,
    fetchEmployerLocations,
    fetchSubContractors,
    fetchSubContractorLocations
  };
};